import { StudyResultListType } from '@/models/studyResultListModel'
import {
  StudyResultDetailType,
  StudyResultDetailScore,
  StudyResultLastMinuteCheckType,
  StudyResultResolveType,
} from '@/models/studyResultDetailModel'
import { ApiResponseStudentStudyContent } from '@/models/apiResponses/studentStudyContents'
import {
  ApiResponseStudyResultDetails,
  ApiResponseStudyResultLastMinuteCheck,
  ApiResponseStudyResultResolve,
} from '@/models/apiResponses/studyResultDetails'
import { CommonCharacterSetting, ResultSetting } from '@/const/DisplaySettings'
import katex from 'katex'

export class StudyResultUtils {
  /**
   * テスト結果一覧を表示用に変換する
   * @param apiResponse API受信した値
   * @returns テスト結果一覧
   */
  public static formatStudyResultList(apiResponse: ApiResponseStudentStudyContent[]): StudyResultListType[] | null {
    if (!apiResponse) return null

    const studyResultList: StudyResultListType[] = apiResponse.map((studentStudyContent) => {
      return {
        title: studentStudyContent.name,
        originalCurriculumId: studentStudyContent.originalCurriculumId,
        summaries: Object.entries(studentStudyContent.curriculumSUnits).map(([, curriculumSUnit]) => {
          return {
            id: curriculumSUnit.paperTestId ? Number(curriculumSUnit.paperTestId) : null,
            subject: curriculumSUnit.subjectName,
            sUnit: curriculumSUnit.curriculumName,
            lastMinuteCheckSUnitId: Number(curriculumSUnit.ids[0][0]),
            lastMinuteCheckResult: curriculumSUnit.ids[0][2]
              ? `${curriculumSUnit.ids[0][1]}問正解/${curriculumSUnit.ids[0][2]}問中`
              : null,
            resolveSUnitId: Number(curriculumSUnit.ids[1][0]),
            resolveResult: curriculumSUnit.ids[1][2]
              ? `${curriculumSUnit.ids[1][1]}問正解/${curriculumSUnit.ids[1][2]}問中`
              : null,
          }
        }),
      }
    })

    return studyResultList
  }

  /**
   * LaTeX形式（HTML）変換
   * @param isLatex LaTeX形式判定
   * @param answer 解答文字列
   * @returns LaTeX形式変換文字列
   */
  private static covertLatexAnswer(isLatex: boolean, answer: string): any {
    return isLatex ? katex.renderToString(answer) : answer
  }

  /**
   * 設問番号テキスト生成
   * @param questionLabel 設問出題ラベル
   * @param questionNumber 設問出題番号
   * @returns 設問番号テキスト
   */
  private static buildQuestionNumberText(questionLabel: string | null, questionNumber: number): string {
    return questionLabel === null || questionLabel === 'NOLABEL'
      ? `${ResultSetting.QUESTION_NUMBER_BRACKETS[0]}${questionNumber}${ResultSetting.QUESTION_NUMBER_BRACKETS[1]}`
      : questionLabel
  }

  /**
   * 履歴画面のテスト直前チェック結果一覧を生成する
   * @param apiResponse テスト直前チェック結果APIレスポンス
   * @returns 履歴画面のテスト直前チェック結果一覧
   */
  public static buildStudyResultLastMinuteChecks(
    apiResponse: ApiResponseStudyResultLastMinuteCheck
  ): StudyResultLastMinuteCheckType[] {
    if (!apiResponse) return []

    const separator = CommonCharacterSetting.ANSWER_SEPARATOR
    return apiResponse.results.map((result) => {
      return {
        problemNumberText: `${ResultSetting.PROBLEM_NUMBER_LABEL}${result.problemNumber}`,
        questionNumberText: this.buildQuestionNumberText(result.questionLabel, result.questionNumber),
        correctSymbol: result.correctSymbolText || CommonCharacterSetting.EMPTY_CELL_LABEL,
        correctAnswer: this.covertLatexAnswer(result.isLatex, result.correctAnswers.join(separator)),
        previousCorrectSymbol: result.previousCorrectSymbol || CommonCharacterSetting.EMPTY_CELL_LABEL,
        previousTwoCorrectSymbol: result.previousTwoCorrectSymbol || CommonCharacterSetting.EMPTY_CELL_LABEL,
        isNeedCheck: result.isNeedCheck || false,
      }
    })
  }

  /**
   * 履歴画面のテスト直前チェックタイトルを取得する
   * @param apiResponse テスト直前チェック結果APIレスポンス
   * @returns 履歴画面のテスト直前チェックタイトル
   */
  public static getStudyResultLastMinuteCheckTitle(apiResponse: ApiResponseStudyResultLastMinuteCheck): string {
    if (!apiResponse) return ''

    return apiResponse.studyName
  }

  /**
   * 履歴画面のテスト解きなおし詳細一覧を生成する
   * @param apiResponse テスト解きなおしAPIレスポンス
   * @returns 履歴画面のテスト解きなおし詳細一覧
   */
  public static buildStudyResultResolves(apiResponse: ApiResponseStudyResultResolve): StudyResultResolveType[] {
    if (!apiResponse) return []

    const separator = CommonCharacterSetting.ANSWER_SEPARATOR
    return apiResponse.results.map((result) => {
      return {
        problemNumber: result.problemNumber,
        problemNumberText: `${ResultSetting.PROBLEM_NUMBER_LABEL}${result.problemNumber}`,
        questionNumber: result.questionNumber,
        questionNumberText: this.buildQuestionNumberText(result.questionLabel, result.questionNumber),
        correctSymbol: result.correctSymbol,
        questionType: result.questionType || CommonCharacterSetting.EMPTY_CELL_LABEL,
        correctAnswer: this.covertLatexAnswer(result.isLatex, result.correctAnswers.join(separator)),
        resolveCorrectSymbol: result.resolveCorrectSymbol,
        isChanged: result.isChanged,
      }
    })
  }

  /**
   * 履歴画面のテスト解きなおしタイトルを取得する
   * @param apiResponse テスト解きなおしAPIレスポンス
   * @returns 履歴画面のテスト解きなおしタイトル
   */
  public static getStudyResultResolveTitle(apiResponse: ApiResponseStudyResultResolve): string {
    if (!apiResponse) return ''

    return apiResponse.studyName
  }

  /**
   * 履歴画面のテスト詳細得点情報を生成する
   * @param apiResponse テスト詳細APIレスポンス
   * @returns 履歴画面のテスト詳細得点情報
   */
  public static buildStudyResultDetailScore(apiResponse: ApiResponseStudyResultDetails): StudyResultDetailScore | null {
    if (!apiResponse) return null

    return {
      totalScore: `${apiResponse.studyResultDetailScore.totalScore}点`,
      knowledgeScore: `${apiResponse.studyResultDetailScore.knowledgeScore}点`,
      thinkingScore: `${apiResponse.studyResultDetailScore.thinkingScore}点`,
      totalAverage: `${apiResponse.studyResultDetailScore.totalAverage || 0}点`,
      knowledgeAverage: `${apiResponse.studyResultDetailScore.knowledgeAverage || 0}点`,
      thinkingAverage: `${apiResponse.studyResultDetailScore.thinkingAverage || 0}点`,
    }
  }

  /**
   * 履歴画面のテスト詳細一覧を生成する
   * @param apiResponse テスト詳細APIレスポンス
   * @returns 履歴画面のテスト詳細一覧
   */
  public static buildStudyResultDetails(apiResponse: ApiResponseStudyResultDetails): StudyResultDetailType[] {
    if (!apiResponse) return []

    return apiResponse.studyResultDetail.map((detail) => {
      return {
        problemNumberText: `${ResultSetting.PROBLEM_NUMBER_LABEL}${detail.problemNumber}`,
        questionNumberText: this.buildQuestionNumberText(detail.questionLabel, detail.questionNumber),
        correctSymbol: detail.correctSymbol,
        correctRate: `${detail.correctRate || 0}%`,
      }
    })
  }

  /**
   * 履歴画面のテスト結果詳細タイトルを取得する
   * @param apiResponse テスト詳細APIレスポンス
   * @returns 履歴画面のテスト結果詳細タイトル
   */
  public static getStudyResultDetailTitle(apiResponse: ApiResponseStudyResultDetails): string {
    if (!apiResponse) return ''

    return apiResponse.studyName
  }

  /**
   * 履歴画面のアドバイスメッセージを取得する
   * @param apiResponse テスト詳細APIレスポンス
   * @returns 履歴画面のアドバイスメッセージ
   */
  public static getAdviceMessage(apiResponse: ApiResponseStudyResultDetails): string {
    if (!apiResponse) return ''

    return apiResponse.studyAdviceMessage
  }

  /**
   * 履歴画面の動画URLを取得する
   * @param apiResponse テスト詳細APIレスポンス
   * @returns 履歴画面の動画URL
   */
  public static getMovieUrl(apiResponse: ApiResponseStudyResultDetails): string {
    if (!apiResponse) return ''

    return apiResponse.movieUrl
  }

  /**
   * ヘッダー情報からファイル名を取得する
   * @param headers ヘッダー情報
   * @returns ファイル名
   */
  public static getFilename(headers: { [headerKey: string]: string }): string | undefined {
    const filenameRegex = /filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/
    const matches = filenameRegex.exec(headers['content-disposition'])
    if (matches != null && matches[1]) {
      return decodeURI(matches[1].replace(/['"]/g, '').replace('utf-8', ''))
    }
  }
}
